import {
  CommentModel,
  DocumentModel,
  TableModel,
  TrackedChangeModel,
} from "../../features/draft/types/document";
import { JyloDocumentDetector, JyloDocumentResult } from "./assembly";

// Re-export types for convenience
export type { JyloDocumentResult } from "./assembly";

export async function insertSDT(label: string, id: string) {
  // Insert a content control (SDT) with custom label and ID and blue styling.
  try {
    await Word.run(async (context) => {
      let selection = context.document.getSelection();

      // Load and store original font properties
      selection.load("font/color,font/bold");
      await context.sync();

      const originalFont = {
        color: selection.font.color,
        bold: selection.font.bold,
      };

      let displayLabel = label;
      if (label.length > 20) {
        displayLabel = label.substring(0, 17) + "...";
      }

      // Add space before
      selection.insertText(" ", Word.InsertLocation.before);

      // Create content control with the tag
      let contentControl = selection.insertContentControl();
      contentControl.title = label;
      contentControl.tag = id;
      contentControl.insertText(`{${displayLabel}}`, "Replace");
      contentControl.cannotDelete = false;
      contentControl.font.color = "#0078d4";
      contentControl.font.bold = true;
      contentControl.appearance = Word.ContentControlAppearance.boundingBox;
      contentControl.cannotEdit = true;

      // Insert space after and position cursor there
      const afterRange = contentControl.getRange(Word.RangeLocation.after);
      afterRange.insertText(" ", Word.InsertLocation.start);
      afterRange.select();

      // Explicitly reset font properties to original values
      const newSelection = context.document.getSelection();
      newSelection.font.color = originalFont.color;
      newSelection.font.bold = originalFont.bold;

      await context.sync();
    });
  } catch (error) {
    console.log("Error: " + error);
  }
}

export async function isJyloDocument(): Promise<JyloDocumentResult> {
  return JyloDocumentDetector.isJyloDocument();
}

export interface DocumentReference {
  id: string;
  label: string;
  stableObjectIdentifier: string;
}

export async function getExistingReferences(): Promise<DocumentReference[]> {
  try {
    return await Word.run(async (context) => {
      const contentControls = context.document.contentControls;
      context.load(contentControls, "title,tag");
      await context.sync();

      const references: DocumentReference[] = [];

      for (let i = 0; i < contentControls.items.length; i++) {
        const control = contentControls.items[i];
        if (control.tag && control.title) {
          references.push({
            id: control.tag,
            label: control.title,
            stableObjectIdentifier: control.tag,
          });
        }
      }

      return references;
    });
  } catch (error) {
    console.log("Error getting existing references: " + error);
    return [];
  }
}

export async function updateExistingSDT(
  stableObjectIdentifier: string,
  newLabel: string
): Promise<boolean> {
  try {
    return await Word.run(async (context) => {
      const contentControls = context.document.contentControls;
      context.load(contentControls, "title,tag");
      await context.sync();

      for (let i = 0; i < contentControls.items.length; i++) {
        const control = contentControls.items[i];
        if (control.tag === stableObjectIdentifier) {
          const displayLabel = newLabel.length > 20 ? newLabel.substring(0, 17) + "..." : newLabel;

          control.title = newLabel;
          control.insertText(`{${displayLabel}}`, "Replace");
          // eslint-disable-next-line office-addins/no-context-sync-in-loop -- runs once; the loop returns immediately after it.
          await context.sync();
          return true;
        }
      }

      return false; // SDT not found
    });
  } catch (error) {
    console.log("Error updating existing SDT: " + error);
    return false;
  }
}

export async function deleteExistingSDT(stableObjectIdentifier: string): Promise<boolean> {
  try {
    return await Word.run(async (context) => {
      const contentControls = context.document.contentControls;
      context.load(contentControls, "title,tag");
      await context.sync();

      for (let i = 0; i < contentControls.items.length; i++) {
        const control = contentControls.items[i];
        if (control.tag === stableObjectIdentifier) {
          control.delete(false); // false means keep the content
          // eslint-disable-next-line office-addins/no-context-sync-in-loop -- runs once; the loop returns immediately after it.
          await context.sync();
          return true;
        }
      }

      return false; // SDT not found
    });
  } catch (error) {
    console.log("Error deleting existing SDT: " + error);
    return false;
  }
}

export async function selectExistingSDT(stableObjectIdentifier: string): Promise<boolean> {
  try {
    return await Word.run(async (context) => {
      const contentControls = context.document.contentControls;
      context.load(contentControls, "title,tag");
      await context.sync();

      for (let i = 0; i < contentControls.items.length; i++) {
        const control = contentControls.items[i];
        if (control.tag === stableObjectIdentifier) {
          control.select();
          // eslint-disable-next-line office-addins/no-context-sync-in-loop -- runs once; the loop returns immediately after it.
          await context.sync();
          return true;
        }
      }

      return false; // SDT not found
    });
  } catch (error) {
    console.log("Error selecting existing SDT: " + error);
    return false;
  }
}

export async function findSelectedReference(): Promise<DocumentReference | null> {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection();
      // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read -- takes a child proxy, reads no value; loaded and synced below.
      const contentControls = selection.contentControls;
      context.load(contentControls, "title,tag");
      await context.sync();

      // Check if there's a content control in the selection
      if (contentControls.items.length > 0) {
        const control = contentControls.items[0];
        console.log("Found content control in selection:", control.title, control.tag);
        if (control.tag && control.title) {
          return {
            id: control.tag,
            label: control.title,
            stableObjectIdentifier: control.tag,
          };
        }
      }

      // If no content control in selection, check if cursor is inside one
      try {
        const range = selection.getRange();
        // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read -- takes a child proxy, reads no value; loaded and synced below.
        const parentContentControl = range.parentContentControl;
        if (parentContentControl) {
          context.load(parentContentControl, "title,tag");
          await context.sync();

          console.log(
            "Found parent content control:",
            parentContentControl.title,
            parentContentControl.tag
          );
          if (parentContentControl.tag && parentContentControl.title) {
            return {
              id: parentContentControl.tag,
              label: parentContentControl.title,
              stableObjectIdentifier: parentContentControl.tag,
            };
          }
        }
      } catch {
        // Parent content control might not exist, which is normal
        console.log("No parent content control found (normal)");
      }

      return null; // No reference found at cursor position
    });
  } catch (error) {
    console.log("Error finding selected reference: " + error);
    return null;
  }
}

export async function getCurrentDocumentAsBlob(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 }, // 64KB slices
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const file = result.value;
          const slices: ArrayBuffer[] = [];
          const sliceCount = file.sliceCount;
          let currentSlice = 0;

          const getNextSlice = () => {
            file.getSliceAsync(currentSlice, (sliceResult) => {
              if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
                const rawData = sliceResult.value.data;

                // Handle different data formats that Office.js might return
                let arrayBuffer: ArrayBuffer;

                if (rawData instanceof ArrayBuffer) {
                  arrayBuffer = rawData;
                } else if (typeof rawData === "string") {
                  // If it's a base64 string, decode it
                  const binaryString = atob(rawData);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  arrayBuffer = bytes.buffer;
                } else if (Array.isArray(rawData)) {
                  // If it's an array of bytes, convert to ArrayBuffer
                  const bytes = new Uint8Array(rawData);
                  arrayBuffer = bytes.buffer;
                } else if (rawData.buffer instanceof ArrayBuffer) {
                  // If it's a typed array, get the buffer
                  arrayBuffer = rawData.buffer.slice(
                    rawData.byteOffset,
                    rawData.byteOffset + rawData.byteLength
                  );
                } else {
                  console.error("Unknown data format:", rawData);
                  file.closeAsync();
                  reject(new Error("Unknown slice data format"));
                  return;
                }

                slices.push(arrayBuffer);
                currentSlice++;

                if (currentSlice < sliceCount) {
                  getNextSlice();
                } else {
                  const blob = new Blob(slices, {
                    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  });
                  file.closeAsync();
                  resolve(blob);
                }
              } else {
                file.closeAsync();
                reject(new Error("Failed to get document slice: " + sliceResult.error.message));
              }
            });
          };

          getNextSlice();
        } else {
          reject(new Error("Failed to get document file: " + result.error.message));
        }
      }
    );
  });
}

/**
 * Extracts the document by simply iterating paragraphs and assigning sequential IDs.
/**
 * Converts a zero-based index to an alphabetic segment ID.
 * 0 → "pA", 1 → "pB", ..., 25 → "pZ", 26 → "pAA", 27 → "pAB", etc.
 */
function indexToAlphaId(n: number): string {
  let result = "";
  let num = n;
  do {
    result = String.fromCharCode(65 + (num % 26)) + result;
    num = Math.floor(num / 26) - 1;
  } while (num >= 0);
  return `p${result}`;
}

/**
 * Converts an alphabetic segment ID back to a zero-based index.
 * "pA" → 0, "pB" → 1, ..., "pZ" → 25, "pAA" → 26, "pAB" → 27, etc.
 * Returns -1 if the ID is invalid.
 */
function alphaIdToIndex(segmentId: string): number {
  const letters = segmentId.replace(/^p/i, "").toUpperCase();
  if (!letters || !/^[A-Z]+$/.test(letters)) return -1;
  let index = 0;
  for (let i = 0; i < letters.length; i++) {
    index = index * 26 + (letters.charCodeAt(i) - 64);
  }
  return index - 1; // convert from 1-based to 0-based
}

/**
 * Builds the tracked-change model shape sent to the assistant from a loaded
 * Word.TrackedChange. TrackedChange.text returns "" for deletions on current Word
 * builds (OfficeDev/office-js#5188), so the caller passes the change range's text
 * as a fallback.
 */
function buildTrackedChangeModel(tc: Word.TrackedChange, fallbackText: string): TrackedChangeModel {
  const change: TrackedChangeModel = {
    type: tc.type === Word.TrackedChangeType.added ? "insertion" : "deletion",
    text: tc.text || fallbackText || "",
  };
  if (tc.author) change.author = tc.author;
  try {
    if (tc.date && !isNaN(tc.date.getTime())) change.date = tc.date.toISOString();
  } catch {
    // date not available on this host — omit
  }
  return change;
}

/**
 * Reads the document's pending tracked changes (redlines) and attributes each one to
 * EVERY paragraph its range touches, keyed by Paragraph.uniqueLocalId. Range-based
 * attribution is required because anchor-based attribution
 * (paragraph.getTrackedChanges) misses changes that start in a neighbouring
 * paragraph — e.g. a fully deleted clause.
 *
 * Runs in its own Word.run and fails open: getTrackedChanges is known to throw on
 * documents containing move-type revisions (OfficeDev/office-js#5535), and that must
 * not take down the main extraction — the assistant then just gets the plain text
 * without redline metadata.
 */
async function collectTrackedChangesByParagraphId(): Promise<Map<string, TrackedChangeModel[]>> {
  const changesByParagraphId = new Map<string, TrackedChangeModel[]>();
  try {
    await Word.run(async (context) => {
      const bodyTrackedChanges = context.document.body.getTrackedChanges();
      bodyTrackedChanges.load("items/type, items/author, items/date, items/text");
      await context.sync();

      // Formatting-only revisions carry no text meaning for the assistant
      const relevantChanges = bodyTrackedChanges.items.filter(
        (tc) =>
          tc.type === Word.TrackedChangeType.added || tc.type === Word.TrackedChangeType.deleted
      );
      if (relevantChanges.length === 0) return;

      const changeRanges = relevantChanges.map((tc) => {
        const range = tc.getRange();
        range.load("text");
        // eslint-disable-next-line office-addins/no-navigational-load -- the paragraph ids of each change range are what this needs.
        range.paragraphs.load("items/uniqueLocalId");
        return range;
      });
      await context.sync();

      relevantChanges.forEach((tc, i) => {
        const model = buildTrackedChangeModel(tc, changeRanges[i].text);
        for (const rp of changeRanges[i].paragraphs.items) {
          const existing = changesByParagraphId.get(rp.uniqueLocalId);
          if (existing) {
            existing.push(model);
          } else {
            changesByParagraphId.set(rp.uniqueLocalId, [model]);
          }
        }
      });
    });
  } catch (err) {
    console.warn("Tracked changes could not be read for this document:", err);
    changesByParagraphId.clear();
  }
  return changesByParagraphId;
}

/**
 * IDs are positional (pA, pB, pC...) and regenerated fresh each call.
 */
export async function extractStructuredDocument(): Promise<DocumentModel> {
  const docModel: DocumentModel = { segments: [], tables: [] };

  // Redline metadata is collected first, in its own Word.run, so any tracked-changes
  // API failure cannot poison the main extraction. getTrackedChanges/uniqueLocalId
  // require WordApi 1.6 — older hosts degrade silently to plain-text extraction.
  const supportsTrackedChanges = Office.context.requirements.isSetSupported("WordApi", "1.6");
  const changesByParagraphId = supportsTrackedChanges
    ? await collectTrackedChangesByParagraphId()
    : new Map<string, TrackedChangeModel[]>();
  const hasTrackedChanges = changesByParagraphId.size > 0;

  await Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load(
      hasTrackedChanges
        ? "items/style, items/text, items/parentTableOrNullObject, items/uniqueLocalId"
        : "items/style, items/text, items/parentTableOrNullObject"
    );
    await context.sync();

    const cellParagraphs = new Map<Word.TableCell, Word.ParagraphCollection>();

    /** Pending revisions for a paragraph, or undefined when there are none. */
    const lookupTrackedChanges = (paragraphIds: string[]): TrackedChangeModel[] | undefined => {
      if (!hasTrackedChanges) return undefined;
      const seen = new Set<TrackedChangeModel>();
      for (const id of paragraphIds) {
        for (const model of changesByParagraphId.get(id) ?? []) {
          seen.add(model);
        }
      }
      return seen.size > 0 ? Array.from(seen) : undefined;
    };

    // For each paragraph, try to load its listItem to get the list marker
    for (const p of paragraphs.items) {
      if (p.text && p.text.trim().length > 0) {
        // Request listItem (may be null for non-list paragraphs)
        p.load("listItemOrNullObject, listItemOrNullObject/listString");
      }
    }
    await context.sync();

    // Also load tables from the document body
    const tables = context.document.body.tables;
    tables.load("items");
    await context.sync();

    // Pre-load table row/cell data
    for (const table of tables.items) {
      table.load("rowCount");
      table.rows.load("items");
    }
    await context.sync();

    for (const table of tables.items) {
      for (const row of table.rows.items) {
        row.cells.load("items");
      }
    }
    await context.sync();

    for (const table of tables.items) {
      for (const row of table.rows.items) {
        for (const cell of row.cells.items) {
          cell.load("cellIndex, value");
          cell.body.load("text");
          if (hasTrackedChanges) {
            const cellParas = cell.body.paragraphs;
            cellParas.load("items/uniqueLocalId");
            cellParagraphs.set(cell, cellParas);
          }
        }
      }
    }
    await context.sync();

    // Build a set of paragraph objects that are inside tables so we can skip them
    // (we'll represent tables as their own segments instead)
    const tableParagraphSet = new Set<Word.Paragraph>();
    for (const p of paragraphs.items) {
      if (p.parentTableOrNullObject && !p.parentTableOrNullObject.isNullObject) {
        tableParagraphSet.add(p);
      }
    }

    // Build table models and track which table index each Word.Table maps to
    const tableModels: TableModel[] = [];
    const wordTableToIndex = new Map<Word.Table, number>();
    for (let ti = 0; ti < tables.items.length; ti++) {
      const table = tables.items[ti];
      wordTableToIndex.set(table, ti);
      const cells: TableModel["cells"] = [];
      let maxCol = 0;
      for (let ri = 0; ri < table.rows.items.length; ri++) {
        const row = table.rows.items[ri];
        for (const cell of row.cells.items) {
          const cellText = (cell.body.text || "").trim();
          const cellParas = cellParagraphs.get(cell);
          const cellChanges = lookupTrackedChanges(
            cellParas ? cellParas.items.map((cp) => cp.uniqueLocalId) : []
          );
          cells.push({
            row: ri,
            column: cell.cellIndex,
            text: cellText,
            segmentId: `t${ti}r${ri}c${cell.cellIndex}`,
            ...(cellChanges ? { trackedChanges: cellChanges } : {}),
          });
          if (cell.cellIndex + 1 > maxCol) maxCol = cell.cellIndex + 1;
        }
      }
      tableModels.push({
        tableIndex: ti,
        segmentId: "", // will be filled when we assign segment IDs
        rows: table.rowCount,
        columns: maxCol,
        cells,
      });
    }

    // Now iterate paragraphs, inserting table segments at the right positions.
    // Strategy: iterate body-level content. For each paragraph that's NOT in a table, emit it.
    // Each contiguous group of table paragraphs represents one table — emit a single table
    // segment on the transition from non-table to table paragraphs.
    let segmentIndex = 0;
    let inTable = false;
    let tableEmitCount = 0;

    for (const p of paragraphs.items) {
      // Check if this paragraph is inside a table
      if (tableParagraphSet.has(p)) {
        if (!inTable) {
          // Transition into a new table group — emit the next table segment
          inTable = true;
          if (tableEmitCount < tableModels.length) {
            const tModel = tableModels[tableEmitCount];
            tModel.segmentId = indexToAlphaId(segmentIndex);
            docModel.segments.push({
              segmentId: indexToAlphaId(segmentIndex),
              text: `[Table: ${tModel.rows} rows × ${tModel.columns} columns]`,
              type: "table",
              table: { tableIndex: tableEmitCount, rowIndex: 0, columnIndex: 0 },
            });
            tableEmitCount++;
            segmentIndex++;
          }
        }
        // Skip all table paragraphs (they're represented by the table segment)
        continue;
      }

      inTable = false;

      // Normal (non-table) paragraph
      let listMarker: string | undefined;
      if (p.text && p.text.trim().length > 0) {
        try {
          const li = p.listItemOrNullObject;
          if (li && !li.isNullObject) {
            const marker = li.listString;
            if (marker) {
              listMarker = marker;
            }
          }
        } catch {
          // listItem not available for this paragraph — ignore
        }
      }

      const isEmpty = !p.text || p.text.trim().length === 0;
      const trackedChanges = lookupTrackedChanges(hasTrackedChanges ? [p.uniqueLocalId] : []);

      docModel.segments.push({
        segmentId: indexToAlphaId(segmentIndex),
        text: isEmpty ? "" : p.text,
        style: p.style,
        type: isEmpty ? "empty" : p.style?.startsWith("Heading") ? "heading" : "paragraph",
        ...(listMarker ? { listMarker } : {}),
        ...(trackedChanges ? { trackedChanges } : {}),
        table: null,
      });

      segmentIndex++;
    }

    // If any tables weren't emitted (e.g., if they have no paragraphs), emit them at the end
    for (let ti = tableEmitCount; ti < tableModels.length; ti++) {
      const tModel = tableModels[ti];
      tModel.segmentId = indexToAlphaId(segmentIndex);
      docModel.segments.push({
        segmentId: indexToAlphaId(segmentIndex),
        text: `[Table: ${tModel.rows} rows × ${tModel.columns} columns]`,
        type: "table",
        table: { tableIndex: ti, rowIndex: 0, columnIndex: 0 },
      });
      segmentIndex++;
    }

    docModel.tables = tableModels;
  });

  const comments = await extractComments(docModel);
  if (comments.length > 0) {
    docModel.comments = comments;
  }

  return docModel;
}

/**
 * Resolves a paragraph by its alphabetic segment ID (e.g. "pC"), with an optional
 * paragraphText hint for resilience when indices shift due to prior inserts/deletes.
 *
 * IMPORTANT: Callers must load "parentTableOrNullObject" on the paragraphs collection
 * before calling this function, so that table-internal paragraphs can be skipped.
 *
 * Strategy:
 * 1. Walk through paragraphs replicating the segment numbering from extractStructuredDocument
 *    (table-internal paragraphs are skipped; each contiguous table group counts as one segment).
 * 2. If the positional match's text matches paragraphText, return it.
 * 3. Otherwise, fall back to a text-based search among non-table paragraphs.
 */
function resolveParagraph(
  paragraphs: Word.ParagraphCollection,
  segmentId: string,
  paragraphText?: string
): Word.Paragraph | null {
  const targetIndex = alphaIdToIndex(segmentId);
  if (targetIndex < 0) return null;

  const allParagraphs = paragraphs.items;

  // Walk through paragraphs replicating the segment numbering from extractStructuredDocument.
  // Table-internal paragraphs are skipped; each contiguous group of table paragraphs
  // counts as one segment (the table segment) in the index.
  let segmentIndex = 0;
  let inTable = false;
  let matchedParagraph: Word.Paragraph | null = null;

  for (const p of allParagraphs) {
    const parentTable = p.parentTableOrNullObject;
    const isInTable = parentTable && !parentTable.isNullObject;

    if (isInTable) {
      if (!inTable) {
        // Transition into a table group — this is a table segment
        if (segmentIndex === targetIndex) {
          // Target is a table segment, not a paragraph
          break;
        }
        segmentIndex++;
        inTable = true;
      }
      continue;
    }

    // Non-table paragraph
    inTable = false;
    if (segmentIndex === targetIndex) {
      matchedParagraph = p;
      break;
    }
    segmentIndex++;
  }

  // If we found a match by index, verify with text hint
  if (matchedParagraph) {
    if (!paragraphText) return matchedParagraph;

    const normalizedTarget = paragraphText.trim().toLowerCase();
    const normalizedActual = (matchedParagraph.text || "").trim().toLowerCase();
    if (
      normalizedActual === normalizedTarget ||
      normalizedActual.includes(normalizedTarget) ||
      normalizedTarget.includes(normalizedActual)
    ) {
      return matchedParagraph;
    }
    // Text doesn't match — fall through to text-based search
  }

  // Fallback: search non-table paragraphs by text content
  if (paragraphText) {
    const normalizedHint = paragraphText.trim().toLowerCase();
    for (const p of allParagraphs) {
      const parentTable = p.parentTableOrNullObject;
      if (parentTable && !parentTable.isNullObject) continue;

      const normalizedText = (p.text || "").trim().toLowerCase();
      if (
        normalizedText.length > 0 &&
        (normalizedText === normalizedHint || normalizedText.includes(normalizedHint))
      ) {
        return p;
      }
    }
  }

  // Last resort: return positional match even if text didn't match
  return matchedParagraph;
}

// The Office.js search API has a hard 255-character limit on the search string.
// For longer strings we search for a start chunk and an end chunk separately
// and join the matching ranges with expandTo(), verifying the combined text
// equals the full `find` string.
const OFFICE_SEARCH_LIMIT = 255;

type SearchTarget = Pick<Word.Paragraph, "search"> | Pick<Word.Body, "search">;

/**
 * Searches `target` for `find`, working around the 255-character search limit.
 * For strings longer than OFFICE_SEARCH_LIMIT it searches for a start anchor
 * (first 255 chars) and an end anchor (last 255 chars), then expands each
 * matching start+end pair with expandTo() and keeps any whose combined text
 * exactly equals `find`. Must be called inside an active Word.run() context.
 */
async function performSearch(
  target: SearchTarget,
  find: string,
  matchCase: boolean,
  context: Word.RequestContext
): Promise<Word.Range[]> {
  const opts = { matchCase, matchWholeWord: false };

  if (find.length <= OFFICE_SEARCH_LIMIT) {
    const results = target.search(find, opts);
    results.load("items");
    await context.sync();
    return results.items;
  }

  // Long string: use the first and last 255-char slices as anchors
  const startChunk = find.slice(0, OFFICE_SEARCH_LIMIT);
  const endChunk = find.slice(-OFFICE_SEARCH_LIMIT);

  const startResults = target.search(startChunk, opts);
  startResults.load("items");
  await context.sync();
  if (startResults.items.length === 0) return [];

  const endResults = target.search(endChunk, opts);
  endResults.load("items");
  await context.sync();
  if (endResults.items.length === 0) return [];

  // Try every start+end pair; keep those whose expanded range text equals `find`
  const matches: Word.Range[] = [];
  for (const startR of startResults.items) {
    for (const endR of endResults.items) {
      try {
        const fullRange = startR.expandTo(endR);
        fullRange.load("text");
        // eslint-disable-next-line office-addins/no-context-sync-in-loop -- deliberate: expandTo fails at sync for out-of-order pairs, so batching would let one bad pair fail them all.
        await context.sync();
        if (fullRange.text === find) matches.push(fullRange);
      } catch {
        // expandTo throws if ranges are in the wrong order — skip this pair
      }
    }
  }
  return matches;
}

/**
 * Finds a paragraph by its alphabetic segment ID (e.g. "pC") and replaces
 * an exact substring within it. Returns a result indicating success or failure.
 *
 * The segmentId corresponds to the index among non-empty paragraphs
 * (matching the IDs from extractStructuredDocument).
 * paragraphText is an optional hint for resilience when indices shift.
 */
export async function searchAndReplaceInParagraph(
  segmentId: string,
  find: string,
  replace: string,
  paragraphText?: string
): Promise<{ success: boolean; error?: string; warning?: string; bookmarkName?: string }> {
  const index = alphaIdToIndex(segmentId);
  if (index < 0) {
    return { success: false, error: `Invalid segment ID: "${segmentId}"` };
  }

  try {
    return await Word.run(async (context) => {
      // Enable track changes so the edit shows as a revision
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items, text, parentTableOrNullObject");
      await context.sync();

      const targetParagraph = resolveParagraph(paragraphs, segmentId, paragraphText);

      if (!targetParagraph) {
        return { success: false, error: `Segment "${segmentId}" not found in document.` };
      }

      // Search within this specific paragraph for the exact text
      const searchItems = await performSearch(targetParagraph, find, true, context);

      if (searchItems.length === 0) {
        // Fallback: try case-insensitive search
        const fallbackItems = await performSearch(targetParagraph, find, false, context);

        if (fallbackItems.length === 0) {
          return {
            success: false,
            error: `Could not find "${find}" in paragraph ${segmentId}.`,
          };
        }

        // Replace the first case-insensitive match and scroll to it
        // Capture the original range before replacing so the bookmark covers both
        // the deletion (old text) and insertion (new text) tracked changes.
        const originalRange = fallbackItems[0].getRange();
        const replacedRange = fallbackItems[0].insertText(replace, Word.InsertLocation.replace);
        // Expand to cover the full extent (deleted + inserted tracked changes)
        const fullRange = originalRange.expandTo(replacedRange);
        const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        fullRange.insertBookmark(bmName);
        replacedRange.select();
        await context.sync();
        const warning =
          fallbackItems.length > 1
            ? `Warning: found ${fallbackItems.length} case-insensitive matches for "${find}" in paragraph ${segmentId}. Only the first was replaced. Use a more specific find string to target a particular occurrence.`
            : undefined;
        return { success: true, bookmarkName: bmName, warning };
      }

      // Replace the first exact match and scroll to it
      // Capture the original range before replacing so the bookmark covers both
      // the deletion (old text) and insertion (new text) tracked changes.
      const originalRange = searchItems[0].getRange();
      const replacedRange = searchItems[0].insertText(replace, Word.InsertLocation.replace);
      // Expand to cover the full extent (deleted + inserted tracked changes)
      const fullRange = originalRange.expandTo(replacedRange);
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      fullRange.insertBookmark(bmName);
      replacedRange.select();
      await context.sync();
      const warning =
        searchItems.length > 1
          ? `Warning: found ${searchItems.length} matches for "${find}" in paragraph ${segmentId}. Only the first was replaced. Use a more specific find string to target a particular occurrence.`
          : undefined;
      return { success: true, bookmarkName: bmName, warning };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Finds and replaces text within a specific table cell.
 * tableIndex identifies which table (0-based), row and column identify the cell.
 */
export async function searchAndReplaceInTableCell(
  tableIndex: number,
  row: number,
  column: number,
  find: string,
  replace: string
): Promise<{ success: boolean; error?: string; warning?: string; bookmarkName?: string }> {
  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      if (tableIndex < 0 || tableIndex >= tables.items.length) {
        return {
          success: false,
          error: `Table index ${tableIndex} out of range (document has ${tables.items.length} table(s)).`,
        };
      }

      const table = tables.items[tableIndex];
      const cell = table.getCell(row, column);
      cell.body.load("text");
      await context.sync();

      // Handle empty cell or empty find string: just insert the replacement text
      const cellText = cell.body.text.trim();
      if (!find || cellText === "") {
        const insertedRange = cell.body.insertText(replace, Word.InsertLocation.start);
        const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        insertedRange.insertBookmark(bmName);
        insertedRange.select();
        await context.sync();
        return { success: true, bookmarkName: bmName };
      }

      // Search within the cell body
      // eslint-disable-next-line office-addins/load-object-before-read -- cell.body is handed over as a proxy; performSearch loads what it needs.
      const searchItems = await performSearch(cell.body, find, true, context);

      if (searchItems.length === 0) {
        // Fallback: case-insensitive
        // eslint-disable-next-line office-addins/load-object-before-read -- cell.body is handed over as a proxy; performSearch loads what it needs.
        const fallbackItems = await performSearch(cell.body, find, false, context);

        if (fallbackItems.length === 0) {
          return {
            success: false,
            error: `Could not find "${find}" in table ${tableIndex} cell [${row},${column}].`,
          };
        }

        const originalRange = fallbackItems[0].getRange();
        const replacedRange = fallbackItems[0].insertText(replace, Word.InsertLocation.replace);
        const fullRange = originalRange.expandTo(replacedRange);
        const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        fullRange.insertBookmark(bmName);
        replacedRange.select();
        await context.sync();
        const warning =
          fallbackItems.length > 1
            ? `Warning: found ${fallbackItems.length} case-insensitive matches in cell [${row},${column}]. Only the first was replaced.`
            : undefined;
        return { success: true, bookmarkName: bmName, warning };
      }

      const originalRange = searchItems[0].getRange();
      const replacedRange = searchItems[0].insertText(replace, Word.InsertLocation.replace);
      const fullRange = originalRange.expandTo(replacedRange);
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      fullRange.insertBookmark(bmName);
      replacedRange.select();
      await context.sync();
      const warning =
        searchItems.length > 1
          ? `Warning: found ${searchItems.length} matches in cell [${row},${column}]. Only the first was replaced.`
          : undefined;
      return { success: true, bookmarkName: bmName, warning };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Adds rows to a table.
 * position: "end" adds after the last row, "start" adds before the first row,
 * or a number inserts after that zero-based row index.
 */
export async function addTableRows(
  tableIndex: number,
  count: number,
  position: "start" | "end" | number,
  values?: string[][]
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      if (tableIndex < 0 || tableIndex >= tables.items.length) {
        return {
          success: false,
          error: `Table index ${tableIndex} out of range (document has ${tables.items.length} table(s)).`,
        };
      }

      const table = tables.items[tableIndex];
      table.load("rowCount");
      await context.sync();

      let newRows: Word.TableRowCollection;
      if (position === "end") {
        newRows = table.addRows(Word.InsertLocation.end, count, values);
      } else if (position === "start") {
        newRows = table.addRows(Word.InsertLocation.start, count, values);
      } else {
        // Insert after a specific row index
        if (position < 0 || position >= table.rowCount) {
          return {
            success: false,
            error: `Row index ${position} out of range (table has ${table.rowCount} rows).`,
          };
        }
        table.rows.load("items");
        await context.sync();
        const row = table.rows.items[position];
        newRows = row.insertRows(Word.InsertLocation.after, count, values);
      }

      newRows.load("items");
      await context.sync();

      // Bookmark the newly added rows for accept/reject tracking
      let bmName: string | undefined;
      if (newRows.items.length > 0) {
        const firstRow = newRows.items[0];
        const lastRow = newRows.items[newRows.items.length - 1];
        firstRow.cells.load("items");
        lastRow.cells.load("items");
        await context.sync();
        if (firstRow.cells.items.length > 0 && lastRow.cells.items.length > 0) {
          const startRange = firstRow.cells.items[0].body.getRange();
          const endRange = lastRow.cells.items[lastRow.cells.items.length - 1].body.getRange();
          const fullRange = startRange.expandTo(endRange);
          bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
          fullRange.insertBookmark(bmName);
          await context.sync();
        }
      }

      return { success: true, bookmarkName: bmName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Adds columns to a table.
 * position: "end" adds after the last column, "start" adds before the first column,
 * or a number inserts after that zero-based column index.
 */
export async function addTableColumns(
  tableIndex: number,
  count: number,
  position: "start" | "end" | number,
  values?: string[][]
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      if (tableIndex < 0 || tableIndex >= tables.items.length) {
        return {
          success: false,
          error: `Table index ${tableIndex} out of range (document has ${tables.items.length} table(s)).`,
        };
      }

      const table = tables.items[tableIndex];

      // addColumns only supports start/end in the Word JS API
      if (position === "end" || typeof position === "number") {
        table.addColumns(Word.InsertLocation.end, count, values);
      } else {
        table.addColumns(Word.InsertLocation.start, count, values);
      }

      await context.sync();

      // Bookmark the table range for accept/reject tracking
      const tableRange = table.getRange();
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      tableRange.insertBookmark(bmName);
      await context.sync();

      return { success: true, bookmarkName: bmName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Deletes rows from a table starting at the given row index.
 */
export async function deleteTableRows(
  tableIndex: number,
  rowIndex: number,
  count: number
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      if (tableIndex < 0 || tableIndex >= tables.items.length) {
        return {
          success: false,
          error: `Table index ${tableIndex} out of range (document has ${tables.items.length} table(s)).`,
        };
      }

      const table = tables.items[tableIndex];
      table.load("rowCount");
      table.rows.load("items");
      await context.sync();

      if (rowIndex < 0 || rowIndex + count > table.rowCount) {
        return {
          success: false,
          error: `Row range [${rowIndex}..${rowIndex + count - 1}] out of bounds (table has ${table.rowCount} rows).`,
        };
      }

      // Bookmark the table range before deletion so we can track the change
      const tableRange = table.getRange();
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      tableRange.insertBookmark(bmName);
      await context.sync();

      table.deleteRows(rowIndex, count);
      await context.sync();
      return { success: true, bookmarkName: bmName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Deletes columns from a table starting at the given column index.
 */
export async function deleteTableColumns(
  tableIndex: number,
  columnIndex: number,
  count: number
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      if (tableIndex < 0 || tableIndex >= tables.items.length) {
        return {
          success: false,
          error: `Table index ${tableIndex} out of range (document has ${tables.items.length} table(s)).`,
        };
      }

      const table = tables.items[tableIndex];
      table.rows.load("items");
      await context.sync();
      if (table.rows.items.length > 0) {
        table.rows.items[0].cells.load("items");
        await context.sync();
        const colCount = table.rows.items[0].cells.items.length;
        if (columnIndex < 0 || columnIndex + count > colCount) {
          return {
            success: false,
            error: `Column range [${columnIndex}..${columnIndex + count - 1}] out of bounds (table has ${colCount} columns).`,
          };
        }
      }

      // Bookmark the table range before deletion so we can track the change
      const tableRange = table.getRange();
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      tableRange.insertBookmark(bmName);
      await context.sync();

      table.deleteColumns(columnIndex, count);
      await context.sync();
      return { success: true, bookmarkName: bmName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Inserts a brand-new table before or after the paragraph identified by segmentId.
 * The table is tracked as a revision (track changes).
 */
export async function insertTable(
  segmentId: string,
  rows: number,
  columns: number,
  position: "before" | "after",
  values?: string[][],
  paragraphText?: string,
  insertAfterBookmark?: string
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  const index = alphaIdToIndex(segmentId);
  if (index < 0) {
    return { success: false, error: `Invalid segment ID: "${segmentId}"` };
  }

  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items, text, parentTableOrNullObject");
      await context.sync();

      let targetParagraph: Word.Paragraph | null = null;

      if (insertAfterBookmark && position === "after") {
        try {
          const bmRange = context.document.getBookmarkRangeOrNullObject(insertAfterBookmark);
          await context.sync();
          if (!bmRange.isNullObject) {
            const bmParagraphs = bmRange.paragraphs;
            bmParagraphs.load("items");
            await context.sync();
            if (bmParagraphs.items.length > 0) {
              targetParagraph = bmParagraphs.items[0];
            }
          }
        } catch {
          // Bookmark resolution failed — fall back to normal resolution
        }
      }

      if (!targetParagraph) {
        targetParagraph = resolveParagraph(paragraphs, segmentId, paragraphText);
      }

      if (!targetParagraph) {
        return { success: false, error: `Segment "${segmentId}" not found in document.` };
      }

      const insertLocation =
        position === "before" ? Word.InsertLocation.before : Word.InsertLocation.after;

      const table = targetParagraph.insertTable(rows, columns, insertLocation, values);
      table.load("rowCount");
      await context.sync();

      // Bookmark the table range for accept/reject tracking
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      table.getRange().insertBookmark(bmName);
      await context.sync();

      return { success: true, bookmarkName: bmName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Applies formatting (bold, italic, underline, etc.) to a specific text range
 * within a paragraph. The formatting is tracked as a revision.
 */
export async function formatText(
  segmentId: string,
  find: string,
  formatting: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    fontSize?: number;
    fontName?: string;
    fontColor?: string;
    highlightColor?: string;
  },
  paragraphText?: string
): Promise<{ success: boolean; error?: string; bookmarkName?: string; warning?: string }> {
  const index = alphaIdToIndex(segmentId);
  if (index < 0) {
    return { success: false, error: `Invalid segment ID: "${segmentId}"` };
  }

  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items, text, parentTableOrNullObject");
      await context.sync();

      const p = resolveParagraph(paragraphs, segmentId, paragraphText);
      if (!p) {
        return { success: false, error: `Segment "${segmentId}" not found in document.` };
      }

      const results = p.search(find, { matchCase: true });
      results.load("items");
      await context.sync();

      if (results.items.length === 0) {
        return { success: false, error: `Text "${find}" not found in segment ${segmentId}.` };
      }

      let warning: string | undefined;
      if (results.items.length > 1) {
        warning = `Multiple matches found for "${find}" in ${segmentId}. Applied formatting to all ${results.items.length} matches.`;
      }

      // Apply formatting to all matching ranges
      for (const range of results.items) {
        if (formatting.bold !== undefined) range.font.bold = formatting.bold;
        if (formatting.italic !== undefined) range.font.italic = formatting.italic;
        if (formatting.underline !== undefined) {
          range.font.underline = formatting.underline
            ? Word.UnderlineType.single
            : Word.UnderlineType.none;
        }
        if (formatting.strikethrough !== undefined)
          range.font.strikeThrough = formatting.strikethrough;
        if (formatting.fontSize !== undefined) range.font.size = formatting.fontSize;
        if (formatting.fontName !== undefined) range.font.name = formatting.fontName;
        if (formatting.fontColor !== undefined) range.font.color = formatting.fontColor;
        if (formatting.highlightColor !== undefined) {
          range.font.highlightColor = formatting.highlightColor;
        }
      }

      // Bookmark the whole span from the first match to the last: formatting was
      // applied to every match above, so a bookmark around only the first would
      // leave the other matches' revisions unreachable by accept/reject. (Ticket 550)
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const firstMatchRange = results.items[0].getRange();
      const spannedRange =
        results.items.length > 1
          ? firstMatchRange.expandTo(results.items[results.items.length - 1].getRange())
          : firstMatchRange;
      spannedRange.insertBookmark(bmName);
      await context.sync();

      return { success: true, bookmarkName: bmName, warning };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Inserts a new paragraph before or after the paragraph identified by segmentId.
 * The new paragraph is tracked as a revision (track changes).
 * paragraphText is an optional hint for resilience when indices shift.
 * insertAfterBookmark — when multiple paragraphs are inserted "after" the same
 * segment in one batch, pass the bookmark of the previous insert so the new
 * paragraph is placed after it (preserving the intended order).
 */
export async function insertParagraph(
  segmentId: string,
  text: string,
  position: "before" | "after",
  style?: string,
  paragraphText?: string,
  insertAfterBookmark?: string,
  listType?: "bullet" | "numbered"
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  const index = alphaIdToIndex(segmentId);
  if (index < 0) {
    return { success: false, error: `Invalid segment ID: "${segmentId}"` };
  }

  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items, text, style, parentTableOrNullObject");
      await context.sync();

      let targetParagraph: Word.Paragraph | null = null;

      // When chaining multiple "after" inserts on the same segment, resolve
      // the target from the bookmark of the previous insert so they stay in order.
      if (insertAfterBookmark && position === "after") {
        try {
          const bmRange = context.document.getBookmarkRangeOrNullObject(insertAfterBookmark);
          await context.sync();
          if (!bmRange.isNullObject) {
            const bmParagraphs = bmRange.paragraphs;
            bmParagraphs.load("items");
            await context.sync();
            if (bmParagraphs.items.length > 0) {
              targetParagraph = bmParagraphs.items[0];
            }
          }
        } catch {
          // Bookmark resolution failed — fall back to normal resolution
        }
      }

      if (!targetParagraph) {
        targetParagraph = resolveParagraph(paragraphs, segmentId, paragraphText);
      }

      if (!targetParagraph) {
        return { success: false, error: `Segment "${segmentId}" not found in document.` };
      }

      const insertLocation =
        position === "before" ? Word.InsertLocation.before : Word.InsertLocation.after;
      const newParagraph = targetParagraph.insertParagraph(text, insertLocation);

      if (style) {
        newParagraph.style = style;
      }

      // If the reference paragraph is a list item and the new paragraph should join the list,
      // attach it to the same list at the same level so it gets proper numbering/bullets.
      // When chaining via insertAfterBookmark, the targetParagraph is the previously inserted
      // paragraph — which may or may not be a list item yet. If it isn't, fall back to the
      // original segment paragraph for list context.
      try {
        const shouldAttach = !style || style.toLowerCase() === "list paragraph";
        if (shouldAttach) {
          let listId: number | null = null;
          let listLevel: number = 0;

          // First, check the target paragraph for list info
          const refListItem = targetParagraph.listItemOrNullObject;
          refListItem.load("level");
          const refList = targetParagraph.listOrNullObject;
          refList.load("id");
          await context.sync();

          if (!refList.isNullObject && !refListItem.isNullObject) {
            listId = refList.id;
            listLevel = refListItem.level;
          }

          // If the target wasn't a list item and we resolved via bookmark (chaining),
          // try the original segment paragraph as a fallback for list context.
          if (listId === null && insertAfterBookmark) {
            const originalParagraph = resolveParagraph(paragraphs, segmentId, paragraphText);
            if (originalParagraph) {
              const origListItem = originalParagraph.listItemOrNullObject;
              origListItem.load("level");
              const origList = originalParagraph.listOrNullObject;
              origList.load("id");
              await context.sync();

              if (!origList.isNullObject && !origListItem.isNullObject) {
                listId = origList.id;
                listLevel = origListItem.level;
              }
            }
          }

          if (listId !== null) {
            newParagraph.attachToList(listId, listLevel);
          } else {
            // No existing list found — start a brand new list so the paragraph
            // gets actual Word numbering/bullets instead of just indentation.
            const newList = newParagraph.startNewList();
            await context.sync();

            // Configure the list type. Default to bullet if not specified.
            if (listType === "numbered") {
              newList.setLevelNumbering(0, Word.ListNumbering.arabic);
            } else {
              newList.setLevelBullet(0, Word.ListBullet.solid);
            }
          }
        }
      } catch {
        // List attachment not supported or failed — paragraph is still inserted, just without list formatting
      }

      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      newParagraph.getRange().insertBookmark(bmName);

      newParagraph.select();
      await context.sync();
      return { success: true, bookmarkName: bmName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Inserts multiple paragraphs in sequence relative to a reference segment,
 * automatically chaining each insertion via bookmarks so they appear in the
 * correct order.  The first paragraph is inserted at `position` relative to
 * `segmentId`; every subsequent paragraph is inserted "after" the previously
 * inserted one using bookmark chaining.
 */
export async function insertChainedParagraphs(
  segmentId: string,
  paragraphs: Array<{ text: string; style?: string; listType?: "bullet" | "numbered" }>,
  position: "before" | "after",
  paragraphText?: string,
  insertAfterBookmark?: string
): Promise<{ success: boolean; error?: string; bookmarkName?: string; bookmarkNames?: string[] }> {
  if (paragraphs.length === 0) {
    return { success: false, error: "No paragraphs provided." };
  }

  const bookmarkNames: string[] = [];

  for (let i = 0; i < paragraphs.length; i++) {
    const para = paragraphs[i];
    const lastBookmark = bookmarkNames[bookmarkNames.length - 1];
    // For the first paragraph: if a prior batch left a bookmark, chain off it
    // (always "after" that bookmark). Otherwise use the caller-supplied position.
    const firstParaPosition: "before" | "after" =
      i === 0 && insertAfterBookmark ? "after" : i === 0 ? position : "after";
    const firstParaBookmark: string | undefined = i === 0 ? insertAfterBookmark : lastBookmark;
    const result = await insertParagraph(
      segmentId,
      para.text,
      firstParaPosition,
      para.style,
      paragraphText,
      firstParaBookmark,
      para.listType
    );

    if (!result.success) {
      return { success: false, error: `Failed at paragraph ${i + 1}: ${result.error}` };
    }

    if (result.bookmarkName) bookmarkNames.push(result.bookmarkName);
  }

  return {
    success: true,
    bookmarkName: bookmarkNames[bookmarkNames.length - 1],
    bookmarkNames,
  };
}

/**
 * Deletes the entire paragraph identified by segmentId.
 * The deletion is tracked as a revision (track changes).
 * paragraphText is an optional hint for resilience when indices shift.
 */
export async function deleteParagraph(
  segmentId: string,
  paragraphText?: string
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  const index = alphaIdToIndex(segmentId);
  if (index < 0) {
    return { success: false, error: `Invalid segment ID: "${segmentId}"` };
  }

  try {
    return await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items, text, parentTableOrNullObject");
      await context.sync();

      const targetParagraph = resolveParagraph(paragraphs, segmentId, paragraphText);

      if (!targetParagraph) {
        return { success: false, error: `Segment "${segmentId}" not found in document.` };
      }

      // Bookmark the range before deleting so we can find the tracked change
      const bmName = `jylo_edit_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      targetParagraph.getRange().insertBookmark(bmName);
      await context.sync();

      // Delete the paragraph range (the entire paragraph including its paragraph mark)
      targetParagraph.getRange().delete();
      await context.sync();
      return { success: true, bookmarkName: bmName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Scrolls to a paragraph by its alphabetic segment ID (e.g. "pC") and selects it.
 * Also inserts a bookmark so the location can be revisited even after document changes.
 * paragraphText is an optional hint for resilience when indices shift.
 */
export async function scrollToParagraph(
  segmentId: string,
  paragraphText?: string,
  bookmarkOnly?: boolean
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  const index = alphaIdToIndex(segmentId);
  if (index < 0) {
    return { success: false, error: `Invalid segment ID: "${segmentId}"` };
  }

  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items, text, parentTableOrNullObject");
      await context.sync();

      const targetParagraph = resolveParagraph(paragraphs, segmentId, paragraphText);

      if (!targetParagraph) {
        return { success: false, error: `Segment "${segmentId}" not found in document.` };
      }

      // Insert a bookmark for persistent navigation
      const bookmarkName = `jylo_scroll_${segmentId}_${Date.now()}`;
      targetParagraph.getRange().insertBookmark(bookmarkName);

      if (!bookmarkOnly) {
        targetParagraph.select();
      }
      await context.sync();
      return { success: true, bookmarkName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Parses a table-cell segment ID like "t0r1c2" into its components.
 * Returns null if the ID doesn't match the expected format.
 */
export function parseTableCellId(
  segmentId: string
): { tableIndex: number; row: number; column: number } | null {
  const m = /^t(\d+)r(\d+)c(\d+)$/.exec(segmentId);
  if (!m) return null;
  return { tableIndex: parseInt(m[1], 10), row: parseInt(m[2], 10), column: parseInt(m[3], 10) };
}

/**
 * Scrolls to a specific table cell and inserts a bookmark for persistent navigation.
 */
export async function scrollToTableCell(
  tableIndex: number,
  row: number,
  column: number,
  bookmarkOnly?: boolean
): Promise<{ success: boolean; error?: string; bookmarkName?: string }> {
  try {
    return await Word.run(async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      if (tableIndex < 0 || tableIndex >= tables.items.length) {
        return { success: false, error: `Table index ${tableIndex} out of range.` };
      }

      const table = tables.items[tableIndex];
      const cell = table.getCell(row, column);
      const range = cell.body.getRange();
      range.load("text");
      await context.sync();

      const bookmarkName = `jylo_scroll_t${tableIndex}r${row}c${column}_${Date.now()}`;
      range.insertBookmark(bookmarkName);
      if (!bookmarkOnly) {
        range.select();
      }
      await context.sync();

      return { success: true, bookmarkName };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Scrolls to a previously inserted bookmark by name.
 * Bookmarks survive text edits and paragraph shifts, making them reliable for navigation.
 */
export async function scrollToBookmark(
  bookmarkName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    return await Word.run(async (context) => {
      const range = context.document.getBookmarkRangeOrNullObject(bookmarkName);
      await context.sync();

      if (range.isNullObject) {
        return { success: false, error: `Bookmark "${bookmarkName}" no longer exists.` };
      }

      range.select();
      await context.sync();
      return { success: true };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/** Result of resolving the tracked changes behind one chat edit. */
export interface ResolveEditResult {
  success: boolean;
  error?: string;
  /** Tracked changes still pending in the bookmark's span. 0 on success. */
  remaining?: number;
}

/**
 * How many accept/reject passes to make before giving up. Word can expose a
 * revision that only becomes reachable once an overlapping one is resolved, so
 * one pass is not always enough; each pass must strictly reduce the count.
 */
const MAX_RESOLVE_PASSES = 3;

/**
 * Accepts or rejects every tracked change in the bookmark's span, and verifies
 * that none is left behind.
 *
 * Uses the collection-level acceptAll/rejectAll (WordApi 1.6) rather than
 * calling accept()/reject() on each item. Per-item resolution cannot work in a
 * batch: every TrackedChange proxy is resolved by document position, and the
 * first one applied shifts the positions the rest resolve against. A text
 * replacement produces two revisions — a deletion of the old run and an
 * insertion of the new — so the insertion would resolve and the deletion would
 * silently survive as strikethrough. (Ticket 550)
 */
async function resolveTrackedChangesAtBookmark(
  bookmarkName: string,
  mode: "accept" | "reject"
): Promise<ResolveEditResult> {
  try {
    // The whole tracked-change path — getTrackedChanges as well as
    // acceptAll/rejectAll — is WordApi 1.6. The manifest sets no <Requirements>,
    // so the add-in loads on older hosts too; guard here rather than at the
    // acceptAll call so the message names the real limitation instead of
    // surfacing a TypeError. Unlike document extraction (which fails open on an
    // unsupported host), resolving a revision must tell the user it did nothing.
    if (!Office.context.requirements.isSetSupported("WordApi", "1.6")) {
      return {
        success: false,
        error: `This version of Word cannot ${mode} revisions from the taskpane (needs Word 2021 or Microsoft 365). Use the Review tab in Word to ${mode} the change.`,
      };
    }

    return await Word.run(async (context) => {
      /** Pending tracked changes in the bookmark's span, or null if the bookmark is gone. */
      const countPending = async (): Promise<number | null> => {
        const range = context.document.getBookmarkRangeOrNullObject(bookmarkName);
        await context.sync();
        if (range.isNullObject) return null;

        const trackedChanges = range.getTrackedChanges();
        trackedChanges.load("items");
        await context.sync();
        return trackedChanges.items.length;
      };

      let previous = Number.POSITIVE_INFINITY;

      for (let pass = 0; pass < MAX_RESOLVE_PASSES; pass++) {
        const range = context.document.getBookmarkRangeOrNullObject(bookmarkName);
        // eslint-disable-next-line office-addins/no-context-sync-in-loop -- a convergence loop, not a per-item one: each pass must observe the count this sync produces before deciding whether to run again.
        await context.sync();

        if (range.isNullObject) {
          // Resolving can remove the whole span the bookmark marked (rejecting an
          // insertion, accepting a deletion), taking the bookmark with it. Gone
          // after a pass has run means done; gone before then is a real failure.
          return pass === 0
            ? { success: false, error: `Bookmark "${bookmarkName}" no longer exists.` }
            : { success: true, remaining: 0 };
        }

        const trackedChanges = range.getTrackedChanges();
        trackedChanges.load("items");
        // eslint-disable-next-line office-addins/no-context-sync-in-loop -- a convergence loop, not a per-item one: each pass must observe the count this sync produces before deciding whether to run again.
        await context.sync();

        const pending = trackedChanges.items.length;
        if (pending === 0) return { success: true, remaining: 0 };

        // No progress: another pass would loop forever. Report the residue so the
        // caller can tell the user rather than claiming the edit was resolved.
        if (pending >= previous) {
          return {
            success: false,
            remaining: pending,
            error: `${pending} tracked change(s) could not be ${mode === "accept" ? "accepted" : "rejected"}. Use Word's Review tab to resolve the remaining revision(s).`,
          };
        }
        previous = pending;

        if (mode === "accept") {
          trackedChanges.acceptAll();
        } else {
          trackedChanges.rejectAll();
        }
        // eslint-disable-next-line office-addins/no-context-sync-in-loop -- a convergence loop, not a per-item one: each pass must observe the count this sync produces before deciding whether to run again.
        await context.sync();
      }

      const remaining = await countPending();
      if (remaining === null || remaining === 0) return { success: true, remaining: 0 };
      return {
        success: false,
        remaining,
        error: `${remaining} tracked change(s) could not be ${mode === "accept" ? "accepted" : "rejected"} after ${MAX_RESOLVE_PASSES} attempts. Use Word's Review tab to resolve the remaining revision(s).`,
      };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}

/**
 * Accepts (finalizes) the tracked change(s) at the given bookmark.
 * After accepting, no revision is left in the bookmark's span; if any survives,
 * the result reports it rather than reporting success.
 */
export async function acceptEdit(bookmarkName: string): Promise<ResolveEditResult> {
  return resolveTrackedChangesAtBookmark(bookmarkName, "accept");
}

/**
 * Rejects (reverts) the tracked change(s) at the given bookmark.
 * The document text is restored to its state before the edit.
 */
export async function rejectEdit(bookmarkName: string): Promise<ResolveEditResult> {
  return resolveTrackedChangesAtBookmark(bookmarkName, "reject");
}

// ── Comments ───────────────────────────────────────────────────────────────────
//
// Reading comments lets the assistant answer questions about review feedback rather
// than only about the text; writing one lets it annotate instead of edit, which is
// often what a user actually wants from a review pass.
//
// Both need WordApi 1.4, which the manifest does not require -- SharedRuntime 1.1 is
// the only floor -- so both check at runtime and degrade rather than throw. A document
// in an older Word simply has no `comments` on its model, and the tool reports why.

const COMMENTS_API = "1.4";

const commentsSupported = (): boolean => {
  try {
    return Office.context.requirements.isSetSupported("WordApi", COMMENTS_API);
  } catch {
    return false;
  }
};

/**
 * Every comment in the document, or an empty list where the API is unavailable.
 *
 * Loads are batched into two syncs regardless of comment count. The obvious shape --
 * loading each comment's range inside the loop -- costs a round trip per comment,
 * which on a marked-up contract is dozens.
 */
export async function extractComments(docModel?: DocumentModel): Promise<CommentModel[]> {
  if (!commentsSupported()) return [];

  try {
    return await Word.run(async (context) => {
      const comments = context.document.body.getRange().getComments();
      comments.load("items/authorName,items/content,items/creationDate");
      await context.sync();

      if (comments.items.length === 0) return [];

      // Queue every range before syncing, not one sync per comment.
      const anchors = comments.items.map((comment) => {
        const range = comment.getRange();
        range.load("text");
        return { comment, range };
      });
      await context.sync();

      return anchors.map(({ comment, range }) => {
        const anchorText = (range.text ?? "").substring(0, 80);
        return {
          segmentId: resolveCommentSegment(anchorText, docModel),
          anchorText,
          text: (comment.content ?? "").trim(),
          author: comment.authorName || undefined,
          date:
            comment.creationDate instanceof Date
              ? comment.creationDate.toISOString().split("T")[0]
              : undefined,
        };
      });
    });
  } catch (error) {
    // A host that reports 1.4 but refuses the call should cost us the comments, not
    // the whole document extract.
    console.log("Error reading comments: " + error);
    return [];
  }
}

/**
 * Which segment a comment belongs to.
 *
 * The API gives a comment's anchor text but not its paragraph, so this matches the
 * anchor back against the extracted segments. Fuzzy by nature: the first segment
 * containing the anchor wins, and identical text in two places resolves to the
 * earlier one. Returns "" rather than guessing when there is no match, and the model
 * is told a blank segmentId means unplaced.
 */
function resolveCommentSegment(anchorText: string, docModel?: DocumentModel): string {
  if (!docModel || !anchorText.trim()) return "";
  const needle = anchorText.trim().toLowerCase();
  const match = docModel.segments.find((segment) => segment.text?.toLowerCase().includes(needle));
  return match?.segmentId ?? "";
}

/** Anchors a new comment to a paragraph. */
export async function addComment(
  segmentId: string,
  commentText: string,
  paragraphText?: string
): Promise<{ success: boolean; error?: string }> {
  if (!commentsSupported()) {
    return {
      success: false,
      error: `Comments need Word API ${COMMENTS_API}, which this version of Word does not support.`,
    };
  }

  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items, text, parentTableOrNullObject");
      await context.sync();

      paragraphs.items.forEach((p) => p.parentTableOrNullObject.load("isNullObject"));
      await context.sync();

      const target = resolveParagraph(paragraphs, segmentId, paragraphText);
      if (!target) {
        return { success: false, error: `Paragraph ${segmentId} not found.` };
      }

      target.getRange().insertComment(commentText);
      await context.sync();
      return { success: true };
    });
  } catch (err) {
    return { success: false, error: `Word API error: ${String(err)}` };
  }
}
